import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from "react"
import useDuplicatedCallback from "../helpers/useDeduplicatedCallback";
import useStateRef from "../helpers/useStateRef";
import useFeature from "../components/Shared/FeatureProvider/useFeature";
import KeyboardWedgeScanner from "./KeyboardWedgeScanner";

// Create Scanning Context
const ScanningContext = createContext({
    scanningCapabilities: 'none',
    displayScanButton: false,
    scanInProgress: false,
    startScan: () => { },
    stopScan: () => { },
    triggerScan: () => { },
    registerCallback: () => { },
    unregisterCallback: () => { },
});

const scanningOptions = { camera:{enabled:true}};
const STORAGE_KEY = 'scanning_options';
const subscribe =  (callback) => {
    window.addEventListener('storage', callback, false)

    return () => {
        window.removeEventListener('storage', callback);
    }
}
const getScanningOptionsStr = () => {
    const optionsString = window.localStorage.getItem(STORAGE_KEY)
    return optionsString ?? JSON.stringify(scanningOptions)

}

const setScanningOptions = (options) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(options))
    window.dispatchEvent(new Event("storage"));
}

const toggleCameraScanning = (enabled) => {
    const optionsStr = getScanningOptionsStr()
    const options = JSON.parse(optionsStr)
    const newOptions = {
        ...options,
        camera: {
            enabled,
        }
    }
    setScanningOptions(newOptions)
}

// Provider Component
export const ScanningProvider = ({ children }) => {
    const [activeCallbacks, setActiveCallbacks] = useState(new Set())
    const [scanInProgress, setScanInProgress, scanInProgressRef] = useStateRef(false);

    const {isEnabled} = useFeature()

    const capabilities = window?.Android?.getScanningCapabilities?.();
    const scanningCapabilities = (capabilities === 'camera' && !isEnabled('camera_scanning'))
        ? 'android'
        : capabilities || 'none';

    const optionsStr = useSyncExternalStore(subscribe, getScanningOptionsStr);

    const options = useMemo(() => {
        try {
        return JSON.parse(optionsStr);
        } catch (e) {
        console.error("Failed to parse scanning options from localStorage", e);
        return scanningOptions;
        }
    }, [optionsStr]);

    const hasActiveCallbacks = activeCallbacks.size > 0;

    const displayScanButton = useMemo(() => scanningCapabilities === 'camera' && hasActiveCallbacks && options.camera.enabled, [scanningCapabilities, hasActiveCallbacks,options]);


    const barcodeCameraEventHandler = useDuplicatedCallback((event) => {
        if (event.value === 'start') {
            setScanInProgress(true);
        }
        else if (event.value === 'stop') {
            setScanInProgress(false);
        }
    },[], 300);

    useEffect(() => {
        window.addEventListener('barcode.camera', barcodeCameraEventHandler, false)

        return () => {
            window.removeEventListener('barcode.camera', barcodeCameraEventHandler);
        }
    }
        , [barcodeCameraEventHandler])

    const startScan = useCallback(() => {
        if (scanningCapabilities === 'camera' && window?.Android?.enableCameraScanning) {
            window.Android.enableCameraScanning();
            setScanInProgress(true);
        }
    }, [scanningCapabilities, setScanInProgress]);

    const stopScan = useCallback(() => {
        if (scanningCapabilities === 'camera' && window?.Android?.disableCameraScanning) {
            window.Android.disableCameraScanning();
            setScanInProgress(false);
        }
    }, [scanningCapabilities, setScanInProgress])

    const registerCallback = useCallback((id) => {
        setActiveCallbacks(prev => new Set(prev).add(id))
    }, [])

    const unregisterCallback = useCallback((id) => {
        setActiveCallbacks(prev => {
            const next = new Set(prev)
            next.delete(id)

            // If no more callbacks and scan is in progress, stop it
            if (next.size === 0 && scanInProgressRef.current) {
                if (scanningCapabilities === 'camera' && window?.Android?.disableCameraScanning) {
                    window.Android.disableCameraScanning();
                    setScanInProgress(false);
                }
            }

            return next
        })
    }, [scanningCapabilities, setScanInProgress])

    const contextValue = useMemo(() => ({
        scanningCapabilities,
        displayScanButton,
        scanInProgress,
        startScan,
        stopScan,
        registerCallback,
        unregisterCallback,
        toggleCameraScanning,
    }), [scanningCapabilities, displayScanButton, scanInProgress, startScan, stopScan, registerCallback, unregisterCallback])

    const handleBarcode = useCallback(({ data }) => {
        const event = new Event("barcode");
        event.value = data;
        event.source = "keyboard-wedge";
        window.dispatchEvent(event);
    }, []);

    return (
        <ScanningContext.Provider value={contextValue}>
            <KeyboardWedgeScanner
            enabled={
                scanningCapabilities === "android" &&
                hasActiveCallbacks
            }
            suffixKeys={["Enter"]}
            allowTimeoutSubmit={false}
            ignoreEditableTargets={true}
            preventDefault={false}
            onBarcode={handleBarcode}
        />
            {children}
        </ScanningContext.Provider>
    )
}

export const useScanningContext = () => useContext(ScanningContext)
